import { FC } from 'react';
import { Form } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Box, Modal as QcsModal } from '@mui/material';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsModalBox } from '../common/basic/QcsModalBox';
import { SectionHeader } from '../common/SectionHeader';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { ShowError } from '../ShowError';
import { IntegrationKeyFields } from './IntegrationKeyFields';
import { getKeyFormInitialValues, IntegrationCredentials, KeyFormValues, keyFormValidation, toApiClientKeyCreateDto } from './integrationUtils';
import { apiClientService } from '@api/services/services';
import { resources } from '@api/resources';
import { ApiClientDetailDto } from '@api/models/apiClient/ApiClientDetailDto';

interface Props {
  detail: ApiClientDetailDto;
  onClose: () => void;
  onAdded: (credentials: IntegrationCredentials) => void;
}

export const IntegrationAddKeyModal: FC<Props> = ({ detail, onClose, onAdded }) => {
  const { t } = useTranslation();

  const handleSubmit = async (values: KeyFormValues) => {
    const res = await apiClientService.addKey(detail.id, toApiClientKeyCreateDto(values));

    onAdded({
      clientName: detail.name,
      clientId: detail.clientId,
      kid: res.data.kid,
      tokenEndpoint: detail.tokenEndpoint,
      apiBase: detail.apiBase,
      docsUrl: detail.docsUrl,
      apiKey: res.data.apiKey ?? undefined,
    });
  };

  return (
    <QcsModal open onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title="integration.apiAccess.keys.add" variant="h5" />
        <FormikDebounce<KeyFormValues>
          initialValues={{ ...getKeyFormInitialValues(), authMethod: detail.authMethod }}
          validationSchema={Yup.object().shape(keyFormValidation(t))}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <IntegrationKeyFields />
              <ShowError resource={resources.addApiClientKey} />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                <QcsButton onClick={onClose}>{t('common.cancel')}</QcsButton>
                <SubmitButton text="integration.apiAccess.keys.add" ignoreMarginLeft />
              </Box>
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
